<template>
  <div class="user-selection-step card-base fade-in">
    <div class="card-body p-4">
      <h4 class="card-title mb-4 text-uppercase">
        <span class="gradient-text">Select</span> <span class="text-primary-color">Support User</span>
      </h4>

      <form @submit.prevent="handleSubmit" class="user-form">
        <div class="row">
          <div class="col">
            <div class="form-group mb-4">
              <div class="form-floating select-container">
                <select 
                  class="form-select custom-select" 
                  v-model="selectedUser"
                  id="userSelect" 
                  aria-label="User selection"
                  required
                >
                  <option value="" disabled selected>Select a user</option>
                  <option v-for="(user, n) in users" :key="n" :value="user">
                    {{ user.name || user.username || user.email }}
                  </option>
                </select>
                <label for="userSelect">Select Support User</label>
                <div class="select-arrow">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z"/>
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="text-center">
          <p class="text-danger error-message" v-if="errorMessage">{{ errorMessage }}</p>
          <button :disabled="loading || !selectedUser" type="submit" class="user-button">
            <span class="button-text">{{ loadText }}</span>
            <span v-if="loading" class="spinner-grow spinner-grow-sm" role="status" aria-hidden="true"></span>
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { Storage } from '@/services/Storage';
import CryptoJS from 'crypto-js';

export default {
  name: 'UserSelectionStep',
  data() {
    return {
      selectedUser: null,
      users: [],
      loading: false,
      errorMessage: ''
    };
  },
  computed: {
    loadText() {
      return this.loading ? 'Please Wait...' : 'Continue';
    }
  },
  async mounted() {
    await this.fetchUsers();
  },
  methods: {
    async fetchUsers() {
      this.loading = true;
      try {
        const response = await axios.get(`${ticketApi}support/getActiveSupportStaff`);
        this.users = response.data;
      } catch (error) {
        this.errorMessage = 'Error fetching users. Please try again.';
        console.error('Error fetching users:', error);
      } finally {
        this.loading = false;
      }
    },

    async handleSubmit() {
      this.loading = true;
      this.errorMessage = '';

      try {
        const payload = {
          userID: this.selectedUser.id,
          storeDB: await this.getStoreDB(),
        };

        // Encrypt payload before storing
        const encryptedPayload = CryptoJS.AES.encrypt(
          JSON.stringify(payload),
          'your-secret-key'
        ).toString();

        localStorage.setItem('users', encryptedPayload);

        // Request OTP
        await axios.post(`${ticketApi}support/requestOTP`, payload);

        // Emit success to parent
        this.$emit('next', { 
          step: 'user-complete', 
          data: { user: this.selectedUser } 
        });
      } catch (error) {
        this.errorMessage = 'Failed to request OTP. Please try again.';
        console.error('Error submitting form:', error);
      } finally {
        this.loading = false;
      }
    },

    async getStoreDB() {
      let store = await Storage.getDefaultStore();
      return store.StoreDB;
    }
  }
};
</script>

<style lang="scss" scoped>
.user-selection-step {
  padding: 0;
  width: 100%;
  background-color: var(--card-bg);
  border: var(--card-border);
  box-shadow: var(--card-shadow);
  border-radius: 16px;
  overflow: visible;
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: var(--card-shadow-hover);
  }
}

.card-body {
  padding: 2rem !important;
}

.card-title {
  font-weight: 700;
  letter-spacing: 1.2px;
  margin-bottom: 2rem;
  text-align: center;
  font-size: 1.5rem;
}

.text-primary-color {
  color: var(--text-primary);
}

.gradient-text {
  background: linear-gradient(to right, var(--accent-electric-blue), var(--accent-neon-purple), var(--accent-electric-blue));
  background-size: 200% 100%;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  opacity: 1;
}

.user-form {
  margin-top: 0;
  opacity: 1;
  transform: translateY(0);
}

.form-group {
  position: relative;
}

.select-container {
  position: relative;
}

.select-arrow {
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  color: var(--accent-electric-blue);
  transition: transform 0.3s ease;

  svg {
    transition: transform 0.3s ease;
  }
}

.custom-select {
  border: var(--card-border);
  background-color: var(--card-bg);
  color: var(--text-primary);
  transition: all 0.3s ease;
  border-radius: 12px;
  padding: 0.75rem 1rem;
  height: 56px;
  font-size: 1rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  cursor: pointer;

  &:focus {
    box-shadow: 0 0 0 3px rgba(var(--accent-electric-blue-rgb, 51, 156, 255), 0.25);
    border-color: var(--accent-electric-blue);
    outline: none;

    & + label + .select-arrow svg {
      transform: rotate(180deg);
    }
  }

  &:hover:not(:focus) {
    border-color: var(--accent-electric-blue);

    & + label + .select-arrow {
      color: var(--accent-neon-purple);
    }
  }

  option {
    padding: 10px;
    font-size: 1rem;
  }
}

.form-floating label {
  padding: 0.75rem 1rem;
  color: var(--text-secondary);
  font-weight: 500;
  pointer-events: none;
}

.error-message {
  min-height: 24px;
  font-size: 0.9rem;
  margin-bottom: 1rem;
}

.user-button {
  width: 100%;
  max-width: 350px;
  height: 56px;
  font-size: 1.1rem;
  font-weight: 600;
  letter-spacing: 1.2px;
  border-radius: 12px;
  position: relative;
  overflow: hidden;
  margin: 1.5rem auto 0;
  background: linear-gradient(to right, var(--accent-electric-blue), var(--accent-neon-purple));
  color: white;
  border: none;
  box-shadow: 0px 2px 8px rgba(var(--accent-electric-blue-rgb, 51, 156, 255), 0.3);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0px 4px 16px rgba(var(--accent-electric-blue-rgb, 51, 156, 255), 0.4);
  }

  &:active:not(:disabled) {
    transform: translateY(1px);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg, 
      rgba(255, 255, 255, 0) 0%, 
      rgba(255, 255, 255, 0.3) 50%, 
      rgba(255, 255, 255, 0) 100%
    );
    transition: all 0.8s ease;
  }

  &:hover:not(:disabled)::before {
    left: 100%;
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }

  .button-text {
    position: relative;
    z-index: 2;
  }

  .spinner-grow {
    margin-left: 8px;
    width: 1rem;
    height: 1rem;
  }
}

/* Animations removed for compatibility with reduced motion settings */

/* Responsive */
@media (max-width: 768px) {
  .card-body {
    padding: 1.5rem !important;
  }

  .card-title {
    font-size: 1.3rem;
  }

  .user-button {
    max-width: 100%;
    font-size: 1rem;
  }
}
</style>
